import React, {Component} from 'react';
import {Button} from 'reactstrap';
import {withTranslation} from 'react-i18next';
import KycLevelForm from './KycLevelForm';
import {reactTableText} from '../constants/configs';
import ModalManager from '../components/modal/ModalManager';
import Rbac from '../components/Rbac';
import ReactTable from 'react-table';
import KycLevelDetails from '../containers/KycLevelDetails';
import FoldableTableHOC from 'react-table/lib/hoc/foldableTable';
import {NotificationManager} from '../components/common/react-notifications';
import {numberWithCommas} from '../helpers/Utils';
import {remove, sortBy, isEqual, map} from 'lodash';
import LoaderButton from '../components/LoaderButton';
import Api from '../services/Api';

const FoldableTable = FoldableTableHOC(ReactTable);

class KycPolicyLevels extends Component {
  constructor(props) {
    super(props);
    this.state = {
      policy: {...props.policy},
      expanded: {},
      submitting: false,
    };
  }

  addLevel = values => {
    const {policy} = this.state;
    this.setState({
      policy: {
        ...policy,
        levels: [
          ...policy.levels,
          {
            ...values,
            level: policy.levels.length + 1,
            items: values.items ? map(values.items, 'value') : []
          }
        ],
      },
    });
  };

  editLevel = (values, level) => {
    const {policy} = this.state;
    const levels = [...policy.levels];
    this.setState({
      policy: {
        ...policy,
        levels: [
          ...remove(levels, obj => obj.level !== level),
          {
            ...values,
            items: values.items ? map(values.items, 'value') : []
          },
        ].sort((a, b) => a.level - b.level),
      }
    });
  };

  deleteLevel = level => {
    const {policy} = this.state;
    const levels = [...policy.levels];
    this.setState({
      policy: {
        ...policy,
        levels: remove(levels, obj => obj.level !== level),
      }
    });
  };

  onPressCreate = () => {
    const {policy} = this.state;
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Create User Level'),
      body: <KycLevelForm
        policy={policy}
        addLevel={this.addLevel}
      />
    });
  };

  onPressEdit = level => {
    const {policy} = this.state;
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Edit User Level'),
      body: <KycLevelForm
        level={level}
        policy={policy}
        editLevel={values => {this.editLevel(values, level.level);}}
      />
    });
  };

  onSubmit = async () => {
    const {policy} = this.state;
    const {t, fireFetchData} = this.props;
    this.setState({submitting: true});
    try {
      await Api.invoke(
        'PATCH',
        '/admin-ui/kyc-policies',
        policy,
      );
      NotificationManager.success(t('Kyc policy levels updated successfully'), t('Message'));
      fireFetchData();
    } catch (e) {
      console.log('error: ', e);
    } finally {
      this.setState({submitting: false});
    }
  };

  getColumns = t => [
    {
      Header: t('Level'),
      accessor: 'level',
      headerClassName: 'text-small',
      width: 50,
    },
    {
      Header: t('Fiat Limits'),
      foldable: true,
      headerClassName: 'text-small',
      columns: [
        {
          Header: t('Deposit 24 hours'),
          accessor: 'fiat.deposit.max_24h',
          headerClassName: 'text-small',
          Cell: props => <span>{props.value ? numberWithCommas(props.value) : t('Infinity')}</span>
        },
        {
          Header: t('Deposit 30 day'),
          accessor: 'fiat.deposit.max_30d',
          headerClassName: 'text-small',
          Cell: props => <span>{props.value ? numberWithCommas(props.value) : t('Infinity')}</span>
        },
        {
          Header: t('Withdraw 24 hours'),
          accessor: 'fiat.withdraw.max_24h',
          headerClassName: 'text-small',
          Cell: props => <span>{props.value ? numberWithCommas(props.value) : t('Infinity')}</span>
        },
        {
          Header: t('Withdraw 30 day'),
          accessor: 'fiat.withdraw.max_30d',
          headerClassName: 'text-small',
          Cell: props => <span>{props.value ? numberWithCommas(props.value) : t('Infinity')}</span>
        }
      ]
    },
    {
      Header: t('Crypto Limits'),
      foldable: true,
      headerClassName: 'text-small',
      columns: [
        {
          Header: t('Deposit 24 hours'),
          accessor: 'crypto.deposit.max_24h',
          headerClassName: 'text-small',
          Cell: props => <span>{props.value ? numberWithCommas(props.value) : t('Infinity')}</span>
        },
        {
          Header: t('Deposit 30 day'),
          accessor: 'crypto.deposit.max_30d',
          headerClassName: 'text-small',
          Cell: props => <span>{props.value ? numberWithCommas(props.value) : t('Infinity')}</span>
        },
        {
          Header: t('Withdraw 24 hours'),
          accessor: 'crypto.withdraw.max_24h',
          headerClassName: 'text-small',
          Cell: props => <span>{props.value ? numberWithCommas(props.value) : t('Infinity')}</span>
        },
        {
          Header: t('Withdraw 30 day'),
          accessor: 'crypto.withdraw.max_30d',
          headerClassName: 'text-small',
          Cell: props => <span>{props.value ? numberWithCommas(props.value) : t('Infinity')}</span>
        }
      ]
    },
    {
      Header: t('Actions'),
      accessor: 'level',
      headerClassName: 'text-small',
      width: 80,
      Cell: props => <div className='d-flex flex-row align-items-center justify-content-between px-2'>
        <span className="text-one text-primary" onClick={() => {this.onPressEdit(props.original);}}>
          <i className="simple-icon-pencil edit-icon cursor-pointer"/>
        </span>
        {props.index === this.state.policy.levels.length - 1 &&
        this.state.policy.levels.length > 1 &&
        <span
          className="text-one text-danger"
          onClick={() => {this.deleteLevel(props.value);}}>
          <i className="simple-icon-trash delete-icon cursor-pointer"/>
        </span>}
      </div>,
      show: Rbac.hasPermission('update_policies'),
    },
  ];

  onExpanded = (newExpanded, index) => {
    this.setState({
      expanded: {
        ...this.state.expanded,
        [index[0]]: !!newExpanded[index[0]],
      },
    });
  };

  render() {
    const {t} = this.props;
    const {expanded, policy, submitting} = this.state;
    return (
      <div className='table-row-sub' style={{paddingRight: 10, paddingLeft: 10}}>
        <div className="text-one text-success d-flex flex-row justify-content-between align-items-center mt-4">
          {t('User Levels')}
          <Rbac permission='update_policies'>
            <Button size='sm' onClick={this.onPressCreate}>
              <i className="simple-icon-plus add-reply-icon"/>
              <span className="mx-1">{t('Create User Level')}</span>
            </Button>
          </Rbac>
        </div>
        <FoldableTable
          manual
          className="-highlight mt-2 mb-2"
          data={sortBy(policy.levels, ['level'])}
          sortable={false}
          resizable={true}
          showPagination={false}
          expanded={expanded}
          columns={this.getColumns(t)}
          onExpandedChange={this.onExpanded}
          pageSize={policy.levels.length || 3}
          style={{width: '100%'}}
          {...reactTableText}
          SubComponent={row => {
            const {original} = row;
            return <KycLevelDetails items={original.items}/>;
          }}
        />
        <div className='mb-4 d-flex flex-row justify-content-end align-items-center'>
          <Rbac permission='update_policies'>
            <LoaderButton
              size='sm'
              color='success'
              style={{width: 140}}
              className='d-flex align-items-center justify-content-center'
              disabled={isEqual(policy, this.props.policy) || submitting}
              loading={submitting}
              onClick={this.onSubmit}>
              {t('Submit')}
            </LoaderButton>
          </Rbac>
        </div>
      </div>
    );
  }
}

export default withTranslation()(KycPolicyLevels);
